import placeholderImg from "@assets/images/placeholder255x255.png";
import NotFoundPage from "@saleor/components/NotFoundPage";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useOnSetDefaultVariant from "@saleor/hooks/useOnSetDefaultVariant";
import useShop from "@saleor/hooks/useShop";
import useUser from "@saleor/hooks/useUser";
import { commonMessages } from "@saleor/intl";
import getTenantIdByUserType from "@saleor/products/utils/getTenantIdByUserType";
import useBrandPrivateSearch from "@saleor/searches/useBrandPrivateSearch";
import {
  useStaffTenantAssignmentQuery,
  useStaffTenantPrivateAssignmentQuery
} from "@saleor/staff/queries";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createMetadataUpdateHandler from "@saleor/utils/handlers/metadataUpdateHandler";
import {
  useMetadataUpdate,
  usePrivateMetadataUpdate
} from "@saleor/utils/metadata/updateMetadata";
import { useWarehouseList } from "@saleor/warehouses/queries";
import { warehouseAddPath } from "@saleor/warehouses/urls";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { useAuth } from "../../auth/AuthProvider";
import { decimal, maybe } from "../../misc";
import ProductVariantDeleteDialog from "../components/ProductVariantDeleteDialog";
import ProductVariantPage, {
  ProductVariantPageSubmitData
} from "../components/ProductVariantPage";
import { ProductsUser } from "../customTypes/ProductsUser";
import useBrandUpdate from "../hooks/useBrandUpdate";
import useProductUserType from "../hooks/useProductUserType";
import useUserViewUpdate from "../hooks/useUserViewUpdate";
import {
  useProductVariantReorderMutation,
  useSimpleProductVariantUpdateMutation,
  useVariantDeleteMutation,
  useVariantImageAssignMutation,
  useVariantImageUnassignMutation,
  useVariantUpdateMutation
} from "../mutations";
import { useProductVariantQuery } from "../queries";
import { VariantUpdate_productVariantUpdate_errors } from "../types/VariantUpdate";
import {
  productUrl,
  productVariantAddUrl,
  productVariantEditUrl,
  ProductVariantEditUrlDialog,
  ProductVariantEditUrlQueryParams
} from "../urls";
import { mapFormsetStockToStockInput } from "../utils/data";
import {
  hasNoError,
  useProductVariantValidation
} from "../utils/productValidations";
import { createVariantReorderHandler } from "./ProductUpdate/handlers";

interface ProductUpdateProps {
  variantId: string;
  productId: string;
  params: ProductVariantEditUrlQueryParams;
}

function getProductVariantQueryVariables(
  id,
  productsUser: ProductsUser,
  tenantData: any
) {
  return {
    avoidAbsolute: productsUser.isSellerMaster,
    id,
    mallID: tenantData && tenantData.mall && tenantData.mall.id,
    withMall: productsUser.isStoreAdmin
  };
}

export const ProductVariant: React.FC<ProductUpdateProps> = ({
  variantId,
  productId,
  params
}) => {
  const {
    user: { userType }
  } = useAuth();
  const shop = useShop();
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();
  const [errors, setErrors] = useState<
    VariantUpdate_productVariantUpdate_errors[]
  >([]);
  useEffect(() => {
    setErrors([]);
  }, [variantId]);

  const [validate, setValidator] = React.useState({
    height: null,
    length: null,
    price: null,
    sku: null,
    weight: null,
    width: null
  });

  const warehouses = useWarehouseList({
    displayLoader: true,
    variables: {
      first: 50
    }
  });

  const { user: userDetail } = useUser();

  // Custom hook for determining if user is KAMS, superuser or regular user
  const productsUser = useProductUserType(params);

  // Hook for updating selected KAMS user view
  const [onUserViewChange, userView, userViews] = useUserViewUpdate(
    userDetail,
    params
  );

  const { result: brandsResult } = useBrandPrivateSearch({
    skip: !productsUser.isKAMS,
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      accountManager: productsUser.id
    }
  });

  const brands = maybe(() => brandsResult?.data?.search?.edges, []).map(
    edge => edge.node
  );

  // Hook for updating of brands
  const [onBrandChange, brand, brandChoices] = useBrandUpdate(
    params,
    brands,
    brandsResult.loading
  );

  const { data: tenantNonKamsData } = useStaffTenantAssignmentQuery({
    skip:
      productsUser.isKAMS ||
      (!productsUser.isKAMS && !productsUser.isStoreAdmin),
    variables: {
      id: getTenantIdByUserType(productsUser, userType, params)
    }
  });

  const { data: tenantKamsData } = useStaffTenantPrivateAssignmentQuery({
    skip:
      !productsUser.isKAMS ||
      (productsUser.isKAMS && !productsUser.isStoreAdmin),
    variables: {
      id: getTenantIdByUserType(productsUser, userType, params)
    }
  });

  const tenantData = productsUser.isKAMS
    ? tenantKamsData && tenantKamsData.tenantPrivate
    : tenantNonKamsData && tenantNonKamsData.tenant;

  const { data, loading } = useProductVariantQuery({
    displayLoader: true,
    variables: getProductVariantQueryVariables(
      variantId,
      productsUser,
      tenantData
    )
  });

  const [updateMetadata] = useMetadataUpdate({});
  const [updatePrivateMetadata] = usePrivateMetadataUpdate({});

  const [openModal] = createDialogActionHandlers<
    ProductVariantEditUrlDialog,
    ProductVariantEditUrlQueryParams
  >(
    navigate,
    params => productVariantEditUrl(productId, variantId, params),
    params
  );

  const handleBack = () => navigate(productUrl(productId));

  const [assignImage, assignImageOpts] = useVariantImageAssignMutation({});
  const [unassignImage, unassignImageOpts] = useVariantImageUnassignMutation(
    {}
  );
  const [deleteVariant, deleteVariantOpts] = useVariantDeleteMutation({
    onCompleted: data => {
      if (data.productVariantDelete.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Variant removed"
          })
        });
      } else if (
        data.productVariantDelete.errors.length !== 0 &&
        data.productVariantDelete.errors[0].field === "variant"
      ) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Product must have at least 1 variant"
          })
        });
      } else if (
        data.productVariantDelete.errors.length !== 0 &&
        data.productVariantDelete.errors[0].field === "productVisibility"
      ) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Cannot delete a published variant"
          })
        });
      } else if (
        data.productVariantDelete.errors.length !== 0 &&
        data.productVariantDelete.errors[0].field === "user"
      ) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "You don't have the permission to delete a variant."
          })
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Something went wrong"
          })
        });
      }
      navigate(productUrl(productId));
    }
  });
  const [updateVariant, updateVariantOpts] = useVariantUpdateMutation({
    onCompleted: data => {
      if (data.productVariantUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        // setErrors(data.productVariantUpdate.errors);
      } else {
        notify({
          status: "error",
          text: data.productVariantUpdate.errors[0].message
        });
      }
      // setErrors(data.productVariantUpdate.errors);
    }
  });

  const [
    simpleProductVariantUpdate /* , simpleProductVariantUpdateOpts*/
  ] = useSimpleProductVariantUpdateMutation({
    onCompleted: data => {
      if (data.productVariantStocksCreate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
      } else {
        notify({
          status: "error",
          text: data.productVariantStocksCreate.errors[0].message
        });
      }
    }
  });

  const variant = data?.productVariant;

  if (variant === null) {
    return <NotFoundPage onBack={handleBack} />;
  }

  const [
    reorderProductVariants,
    reorderProductVariantsOpts
  ] = useProductVariantReorderMutation({});

  const onSetDefaultVariant = useOnSetDefaultVariant(productId, variant);

  const handleVariantReorder = createVariantReorderHandler(
    variant?.product,
    variables => reorderProductVariants({ variables })
  );

  const disableFormSave =
    loading ||
    deleteVariantOpts.loading ||
    updateVariantOpts.loading ||
    assignImageOpts.loading ||
    unassignImageOpts.loading ||
    reorderProductVariantsOpts.loading;

  const handleImageSelect = (id: string) => () => {
    if (variant) {
      if (variant?.images?.map(image => image.id).indexOf(id) !== -1) {
        unassignImage({
          variables: {
            imageId: id,
            variantId: variant.id
          }
        });
      } else {
        assignImage({
          variables: {
            imageId: id,
            variantId: variant.id
          }
        });
      }
    }
  };

  const handleUpdate = async (data: ProductVariantPageSubmitData) => {
    if (productsUser.isSuperUser) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "You don't have the permission to update a product variant."
        })
      });
      return [];
    }

    if (productsUser.isStoreAdmin) {
      const createParams = [
        {
          quantity: data.stockOnMall === "" ? 0 : Number(data.stockOnMall),
          warehouse: tenantData.mall?.firstWarehouse?.id || ""
        }
      ];
      const result = await simpleProductVariantUpdate({
        variables: {
          addStocks: variant.stockOnMall === null ? createParams : [],
          deleteStocks: data.removeStocks,
          productVariantId: variantId,
          updateStocks: variant.stockOnMall === null ? [] : createParams
        }
      });
      return [
        ...result.data?.productVariantStocksCreate.errors,
        ...result.data?.productVariantStocksDelete.errors,
        ...result.data?.productVariantStocksUpdate.errors
      ];
    } else {
      const result = await updateVariant({
        variables: {
          addStocks: data.addStocks.map(mapFormsetStockToStockInput),
          ...(productsUser.isKAMS && { brand: data.brand }),
          costPrice: decimal(data.costPrice),
          height: data.height === "" ? null : data.height,
          id: variantId,
          length: data.length === "" ? null : data.length,
          price: decimal(data.price),
          removeStocks: data.removeStocks,
          sku: data.sku,
          stocks: data.updateStocks.map(mapFormsetStockToStockInput),
          trackInventory: data.trackInventory,
          weight: data.weight === "" ? null : data.weight,
          width: data.width === "" ? null : data.width
        }
      });

      return [
        ...result.data?.productVariantStocksCreate.errors,
        ...result.data?.productVariantStocksDelete.errors,
        ...result.data?.productVariantStocksUpdate.errors,
        ...result.data?.productVariantUpdate.errors
      ];
    }
  };

  const handleValidation = data => {
    const validationResult = useProductVariantValidation(data);
    setValidator(validationResult);
    if (hasNoError(validationResult)) {
      handleSubmit(data);
    }
    // handleUpdate(data); STG replaces Handlesubmit with HandleUpdate
  };
  const handleSubmit = createMetadataUpdateHandler(
    data?.productVariant,
    handleUpdate,
    variables => updateMetadata({ variables }),
    variables => updatePrivateMetadata({ variables })
  );

  return (
    <>
      <WindowTitle title={data?.productVariant?.sku} />
      <ProductVariantPage
        productsUser={productsUser}
        brand={brand.value}
        brands={brandChoices}
        brandLabel={brand.label}
        onBrandChange={onBrandChange}
        userView={userView.value}
        userViewLabel={userView.label}
        userViews={userViews}
        onUserViewChange={onUserViewChange}
        defaultWeightUnit={shop?.defaultWeightUnit}
        defaultVariantId={data?.productVariant.product.defaultVariant?.id}
        errors={errors}
        onSetDefaultVariant={onSetDefaultVariant}
        saveButtonBarState={updateVariantOpts.status}
        loading={disableFormSave}
        placeholderImage={placeholderImg}
        variant={variant}
        variantyQuantity={maybe(() => variant.stockOnMall)}
        absoluteValue={maybe(() => variant.sellingPrice)}
        header={variant?.sku}
        tenant={maybe(() => tenantData.mall)}
        warehouses={
          warehouses.data?.warehouses.edges.map(edge => edge.node) || []
        }
        onAdd={() => navigate(productVariantAddUrl(productId))}
        onBack={handleBack}
        onDelete={() => openModal("remove")}
        onImageSelect={handleImageSelect}
        onSubmit={handleValidation}
        onWarehouseConfigure={() => navigate(warehouseAddPath)}
        onVariantClick={variantId => {
          navigate(productVariantEditUrl(productId, variantId));
        }}
        onVariantReorder={handleVariantReorder}
        isStoreAdmin={productsUser.isStoreAdmin}
        isSellerMaster={productsUser.isSellerMaster}
        validate={validate}
      />
      <ProductVariantDeleteDialog
        confirmButtonState={deleteVariantOpts.status}
        onClose={() => navigate(productVariantEditUrl(productId, variantId))}
        onConfirm={() =>
          deleteVariant({
            variables: {
              id: variantId
            }
          })
        }
        open={params.action === "remove"}
        name={data?.productVariant?.sku}
      />
    </>
  );
};
export default ProductVariant;
